import { useEffect, useMemo, useState } from "react";
import { Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { mapIntegrationToCampaignOption } from "@/lib/campaign-integration-options";
import { isIntegrationOffline } from "@/lib/uazapi-connection-state";
import type { CampaignIntegrationOption } from "@/lib/automation-campaign";

interface DisparoRescheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  campaignName: string;
  loading: boolean;
  onConfirm: (values: { integration_id: string; scheduled_at: string }) => void;
}

function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

export function DisparoRescheduleDialog({
  open,
  onOpenChange,
  workspaceId,
  campaignName,
  loading,
  onConfirm,
}: DisparoRescheduleDialogProps) {
  const [integrations, setIntegrations] = useState<CampaignIntegrationOption[]>([]);
  const [fetching, setFetching] = useState(false);
  const [integrationId, setIntegrationId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  const minValue = useMemo(() => toLocalInputValue(new Date(Date.now() + 5 * 60_000)), [open]);

  useEffect(() => {
    if (!open || !workspaceId) return;
    let cancelled = false;

    setIntegrationId("");
    setScheduledAt(toLocalInputValue(new Date(Date.now() + 30 * 60_000)));
    setFetching(true);

    (async () => {
      const { data, error } = await supabase
        .from("integrations")
        .select("id, provider, config, instance_identifier, status")
        .eq("workspace_id", workspaceId)
        .eq("provider", "whatsapp_uazapi")
        .eq("status", "active")
        .order("created_at", { ascending: true });

      if (cancelled) return;
      const connected = (data ?? []).filter((row) => !isIntegrationOffline(row));
      if (!error) {
        setIntegrations(
          connected.map((row) =>
            mapIntegrationToCampaignOption(
              row as Parameters<typeof mapIntegrationToCampaignOption>[0],
            ),
          ),
        );
        if (connected.length === 1) setIntegrationId(connected[0].id);
      }
      setFetching(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, workspaceId]);

  const canConfirm =
    !loading && !!integrationId && !!scheduledAt && new Date(scheduledAt).getTime() > Date.now();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reagendar disparo</DialogTitle>
          <DialogDescription>
            A campanha "{campaignName}" foi suspensa porque a conexão caiu. Escolha uma conexão
            conectada e o novo horário. Só os contatos que ainda não receberam serão enviados.
          </DialogDescription>
        </DialogHeader>

        {fetching ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Verificando conexões...
          </div>
        ) : integrations.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            Nenhuma conexão WhatsApp conectada no momento. Reconecte um número em Integrações para
            poder reagendar.
          </p>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Conexão que fará o envio</Label>
              <Select value={integrationId} onValueChange={setIntegrationId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conexão..." />
                </SelectTrigger>
                <SelectContent>
                  {integrations.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      <span className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 shrink-0 text-green-500" />
                        <span className="truncate">
                          {item.display_name}
                          {item.detail ? (
                            <span className="text-xs text-muted-foreground"> ({item.detail})</span>
                          ) : null}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="disparo-reschedule-at">Nova data e hora</Label>
              <Input
                id="disparo-reschedule-at"
                type="datetime-local"
                value={scheduledAt}
                min={minValue}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            disabled={!canConfirm}
            onClick={() =>
              onConfirm({
                integration_id: integrationId,
                scheduled_at: new Date(scheduledAt).toISOString(),
              })
            }
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Reagendar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
